import { XMarkIcon } from "@heroicons/react/24/outline";
import { FC, useMemo, useRef, useState } from "react";
import { IReservationV2, ITicket } from "../../../interfaces/reservations/Reservations.types";
import { eur } from "../../../utils/eur";
import {
  buildCancellationRequest,
  includeChoices,
  previewKey
} from "../../../utils/reservations/cancellation";
import Button from "../../ui/Button";
import IconBtn from "../../ui/IconBtn";
import Input from "../../ui/input/Input";
import Modal from "../../ui/Modal";
import CancellationOptions, { Section } from "./CancellationOptions";
import PayoutTiming from "./PayoutTiming";
import PreviewCard from "./PreviewCard";
import TicketChecklist from "./TicketChecklist";
import useCancellationPreview from "./useCancellationPreview";
import useRefundChoices from "./useRefundChoices";
import useSubmitCancellation from "./useSubmitCancellation";

// One dialog for every admin cancellation: "ticket" cancels the chosen tickets, "booking" all of
// them. The admin picks what is cancelled and what comes back; the server prices it.
const CancellationDialog: FC<{
  booking: IReservationV2;
  mode: "ticket" | "booking";
  initialTicketId?: number;
  /** The tickets still valid; a cancelled ticket cannot be cancelled again. */
  tickets: ITicket[];
  onClose: () => void;
  onDone: () => void;
}> = ({ booking, mode, initialTicketId, tickets, onClose, onDone }) => {
  const whole = mode === "booking";
  const [selectedIds, setSelectedIds] = useState<number[]>(
    whole ? tickets.map((t) => t.ticketId) : initialTicketId ? [initialTicketId] : []
  );
  const choices = useRefundChoices(booking.uniqueJourneyId);
  const { overrides, destination, refund, reason } = choices;
  const [payoutNow, setPayoutNow] = useState(false);

  const request = useMemo(
    () => buildCancellationRequest({ uniqueJourneyId: booking.uniqueJourneyId, ticketIds: selectedIds,
      overrides, destination, reason, refund }),
    [booking.uniqueJourneyId, selectedIds, overrides, destination, reason, refund]
  );
  const { preview, loading, error, reload } = useCancellationPreview(request);
  const { submitting, staleNotice, submit } = useSubmitCancellation(onDone, reload, previewKey(request));
  const includeItems = useMemo(() => includeChoices(booking, preview), [booking, preview]);

  // Closing mid-submit would hide whether the cancellation went through. Read through a ref:
  // Modal binds its Escape handler once, with the first render's close.
  const submittingRef = useRef(submitting);
  submittingRef.current = submitting;
  const close = () => {
    if (!submittingRef.current) onClose();
  };
  const toggleTicket = (ticketId: number) =>
    setSelectedIds((prev) =>
      prev.indexOf(ticketId) >= 0 ? prev.filter((id) => id !== ticketId) : [...prev, ticketId]);

  const nothing = !!preview && preview.toCard <= 0 && preview.toWallet <= 0;
  // "Pa rimbursim" never submits figures that would still pay something back.
  const canSubmit = !!preview && preview.refusals.length === 0 && !loading && !submitting
    && !!request.reason && selectedIds.length > 0 && (refund || nothing);
  const label = submitting ? "Duke kryer…" : loading ? "Duke llogaritur…"
    : !refund ? "Anulo pa rimbursim" : !preview ? "Anulo"
    : nothing ? "Anulo pa rimbursim" : `Anulo dhe rimburso ${eur(preview.value)}`;

  return (
    <Modal show position="center" onClose={close} hasHeader={false} calcHeight={false} fitContent
      modalWidth="640px" ariaLabelledBy="cancellation-title">
      <div className="flex items-start justify-between px-5 pt-5 pb-3">
        <div>
          <h2 id="cancellation-title" className="text-base font-semibold text-black">
            {whole ? "Anulo rezervimin" : "Anulo biletat"}
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">{booking.uniqueJourneyId}</p>
        </div>
        <IconBtn onClick={close} ariaLabel="Mbyll" disabled={submitting}>
          <XMarkIcon className="w-4 h-4" strokeWidth={2} />
        </IconBtn>
      </div>
      <div className="px-5 pb-4 flex flex-col gap-5 text-sm text-gray-700">
        <Section title="Çfarë anulohet">
          <TicketChecklist tickets={tickets} selectedIds={selectedIds} locked={whole}
            initialTicketId={initialTicketId} onToggle={toggleTicket} />
        </Section>
        <CancellationOptions items={includeItems} overrides={overrides} preview={preview}
          onToggle={(item, next) => choices.setOverride(item.key, next)}
          destination={destination} onDestination={choices.changeDestination}
          refund={refund} onRefund={choices.changeRefund} />
        <Section title="Arsyeja">
          <Input id="cancellation-reason" className="w-full" paddingType="small" autoComplete="off"
            value={reason} onChange={(e) => choices.setReason(e.target.value)}
            error={reason.trim() ? undefined : "Arsyeja është e detyrueshme."} />
        </Section>
        <Section title="Rimbursimi">
          <PreviewCard preview={preview} loading={loading} error={error} staleNotice={staleNotice}
            onRetry={reload} noRefund={!refund} wholeBooking={whole} />
          {refund && !!preview && preview.toCard > 0 && (
            <PayoutTiming payoutNow={payoutNow} onChange={setPayoutNow} />
          )}
        </Section>
      </div>
      <div className="sticky bottom-0 border-t border-gray-200 bg-gray-50 px-5 py-3 flex justify-end gap-2">
        <Button btnType="custom" className="border border-gray-300 bg-white" disabled={submitting}
          onClick={close}>
          Mbyll
        </Button>
        <Button disabled={!canSubmit}
          onClick={() => preview && canSubmit && submit(request, preview, payoutNow)}>
          {label}
        </Button>
      </div>
    </Modal>
  );
};

export default CancellationDialog;
